<template>
    <div class="listings-map-app">
        <div v-if="loading" class="text-center p-4">
            <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
            <p>Loading listings...</p>
        </div>

        <div v-else-if="error" class="alert alert-danger">
            <strong>Error:</strong> {{ error }}
        </div>

        <div v-else>


            <!-- Map Component -->
            <div class="col-12 px-3">
                <ListingsMap ref="listingsMap"
                             :markers="mappedListings"
                             :selected-groups="selectedGroups"
                             :center-lat="centerLat"
                             :center-lng="centerLng"
                             :is-authenticated="isAuthenticated"
                             @listing-saved="handleListingSaved" />

                <div v-if="!mappedListings.length" class="content mt-3">
                    <p>No matching garage sales found.</p>
                </div>
            </div>

            <!-- Update Location Component -->
            <div class="col-12 px-3">
                <UpdateLocation @location-updated="handleLocationUpdated"
                                @location-cleared="handleLocationCleared" />
            </div>
        </div>
    </div>
</template>

<script>
    import ListingsMap from '@shared/ListingsMap.vue'
    import UpdateLocation from '@shared/UpdateLocation.vue'

    export default {
        name: 'ListingsMapApp',
        components: {
            ListingsMap,
            UpdateLocation
        },
        props: {
            filter: {
                type: String,
                default: "all"
            },
            isAuthenticated: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                listings: [],
                mappedListings: [],
                selectedGroups: new Set(['default']), // Default group for public listings
                loading: true,
                error: null,
                centerLat: 26.224472,
                centerLng: -98.230002
            }
        },
        mounted() {
            this.loadListingsData()
        },
        methods: {
            async loadListingsData() {
                try {
                    this.loading = true
                    const urlParams = new URLSearchParams(window.location.search)
                    const tags = urlParams.get('tags') || ''

                    const response = await fetch(`/api/UserListings/public-data?filter=${this.filter}&tags=${encodeURIComponent(tags)}`)
                    if (!response.ok) {
                        throw new Error('Failed to load listings data')
                    }

                    const data = await response.json()
                    this.listings = data.listings || []

                    // Create map markers data without grouping - just put all in one group
                    this.mappedListings = this.createMapMarkers(this.listings)
                    
                    // Set center based on first listing if available
                    if (this.listings.length > 0) {
                        this.centerLat = this.listings[0].latitude || this.centerLat
                        this.centerLng = this.listings[0].longitude || this.centerLng
                    }

                } catch (error) {
                    console.error('Error loading listings data:', error)
                    this.error = error.message
                } finally {
                    this.loading = false
                }
            },

            createMapMarkers(listings) {
                let markers = []
                let allListings = {
                    groupId: 'default',
                    name: 'All Listings',
                    width: 20,
                    height: 20,
                    visible: true,
                    shape: 'Image',
                    imageUrl: '/images/locationpin.png',
                    animationDuration: 0, // Disable marker animation
                    dataSource: [],
                    tooltipSettings: {
                        visible: true,
                        duration: 0,
                        template: this.isAuthenticated ? '#unsavedTooltipAuthenticated' : '#unsavedTooltip'
                    },
                };
                markers.push(allListings);

                let savedListings = {
                    groupId: 'saved',
                    name: 'Saved Listings',
                    width: 20,
                    height: 20,
                    visible: true,
                    shape: 'Image',
                    imageUrl: '/images/locationpinselected.png',
                    animationDuration: 0, // Disable marker animation
                    dataSource: [],
                    tooltipSettings: {
                        visible: true,
                        duration: 0,
                        template: "#savedTooltipTemplate"
                    },
                };
                markers.push(savedListings);

                let ownedListings = {
                    groupId: 'owned',
                    name: 'Owned Listings',
                    width: 20,
                    height: 20,
                    visible: true,
                    shape: 'Image',
                    imageUrl: '/images/locationpinmine.png',
                    animationDuration: 0, // Disable marker animation
                    dataSource: [],
                    tooltipSettings: {
                        visible: true,
                        duration: 0,
                        format: "Your Listing"
                    },
                };
                markers.push(ownedListings);

                // loop through each listing
                listings.forEach(listing => {
                    if (listing.ownershipState === 'Unsaved') {
                        allListings.dataSource.push({
                            id: listing.id,
                            title: listing.title || listing.address1,
                            address: listing.address1 + (listing.address2 ? ', ' + listing.address2 : ''),
                            latitude: listing.latitude,
                            longitude: listing.longitude,
                            schedule: `${new Date(listing.startDate).toLocaleDateString()} - ${new Date(listing.endDate).toLocaleDateString()}`,
                            state: listing.ownershipState,
                            url: `listings/${listing.id}/${this.createUrlSlug(listing.address1)}`,
                            groupId: 'unsaved'
                        });
                    } else if (listing.ownershipState === 'Saved') {
                        savedListings.dataSource.push({
                            id: listing.id,
                            title: listing.title || listing.address1,
                            address: listing.address1 + (listing.address2 ? ', ' + listing.address2 : ''),
                            latitude: listing.latitude,
                            longitude: listing.longitude,
                            schedule: `${new Date(listing.startDate).toLocaleDateString()} - ${new Date(listing.endDate).toLocaleDateString()}`,
                            state: listing.ownershipState,
                            url: `listings/${listing.id}/${this.createUrlSlug(listing.address1)}`,
                            groupId: 'saved'
                        });
                    } else if (listing.ownershipState === 'Owned') {
                        ownedListings.dataSource.push({
                            id: listing.id,
                            title: listing.title || listing.address1,
                            address: listing.address1 + (listing.address2 ? ', ' + listing.address2 : ''),
                            latitude: listing.latitude,
                            longitude: listing.longitude,
                            schedule: `${new Date(listing.startDate).toLocaleDateString()} - ${new Date(listing.endDate).toLocaleDateString()}`,
                            state: listing.ownershipState,
                            url: `listings/${listing.id}/${this.createUrlSlug(listing.address1)}`,
                            groupId: 'owned'
                        });
                    }
                })

                return markers;
            },

            createUrlSlug(title) {
                return title.toLowerCase()
                    .replace(/[^\w\s-]/g, '')
                    .replace(/[\s_-]+/g, '-')
                    .replace(/^-+|-+$/g, '')
            },

            handleLocationUpdated(locationData) {
                // Update the map with the new user location
                if (this.$refs.listingsMap) {
                    this.$refs.listingsMap.updateUserLocation(locationData);
                }
            },

            handleLocationCleared() {
                // Clear user location from the map
                if (this.$refs.listingsMap) {
                    this.$refs.listingsMap.clearUserLocation();
                }
            },

            getListingsByType(type) {
                if (!this.mappedListings.length) return [];
                // Only one group, so use mappedListings[0].dataSource
                return this.mappedListings[0].dataSource.filter(l => l.ownershipState === type);
            },

            async handleListingSaved({ listingId, oldState, newState }) {
                try {
                    // Find the listing in our data
                    const listing = this.listings.find(l => l.id === listingId);

                    if (!listing) {
                        console.warn(`Listing ${listingId} not found in local data`);
                        return;
                    }

                    // Update ownership state
                    listing.ownershipState = newState;

                    // Rebuild markers to reflect new state
                    // This will move the marker to the appropriate group (saved/unsaved)
                    // and update the icon (blue pin → selected blue pin or vice versa)
                    this.mappedListings = this.createMapMarkers(this.listings);

                    console.log(`Updated listing ${listingId}: ${oldState} → ${newState}`);
                } catch (error) {
                    console.error('Error handling listing save:', error);
                }
            }
        }
    }
</script>

<style scoped>
    .listings-map-app {
        width: 100%;
    }

    .spinner-border {
        width: 3rem;
        height: 3rem;
    }

    .debug-listings {
        background: #f8f9fa;
        border: 1px solid #ddd;
        padding: 1rem;
        border-radius: 6px;
        font-size: 0.95em;
    }

        .debug-listings ul {
            margin-bottom: 0;
        }
</style>